import * as React from 'react';
import { storyblokEditable, useStoryblokState } from 'gatsby-source-storyblok';

const getStoryblokDimensions = (url) => {
  const [rawWidth, rawHeight] = url?.split('/')[5]?.split('x') || [];
  const width = Number.parseInt(rawWidth, 10);
  const height = Number.parseInt(rawHeight, 10);

  return {
    width: Number.isNaN(width) ? undefined : width,
    height: Number.isNaN(height) ? undefined : height,
  };
};
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import clsx from 'clsx';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon, library } from '@fortawesome/fontawesome-svg-core';
import { useLocation } from '@reach/router';
import * as icons from '../../icons/icon-pack';
import BlogGridView from '../blog-grid-view';
import { handleize } from '../../helpers/handleize';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

function Post({ post }) {
  const storyBlokPost = useStoryblokState(post, {
    resolveRelations: [
      'blogpost.author'],
  });
  const { content } = storyBlokPost;
  const title = content?.title || storyBlokPost.name || storyBlokPost.slug;
  const featuredImg = content?.hero?.filename || '';
  const { width: featuredImgWidth, height: featuredImgHeight } = getStoryblokDimensions(featuredImg);

  return (
    <Link
      key={storyBlokPost.uuid}
      to={`/${storyBlokPost.full_slug}`}
      className="mx-auto aspect-[134/85] w-full relative group overflow-hidden"
    >
      <article
        itemScope
        itemType="http://schema.org/Article"
        className="bg-slate-100 dark:bg-latori-green-dark shadow-lg h-full"
      >
        {featuredImg
          && (
          <img
            src={`${featuredImg}/m/600x0`}
            alt={getStoryblokAlt(content?.hero, storyBlokPost.name || storyBlokPost.slug)}
            width={featuredImgWidth}
            height={featuredImgHeight}
            className="w-full h-full object-cover absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
            loading="lazy"
          />
          )}
        <div className="bg-latori-green-dark opacity-90 w-full h-full absolute bottom-0 left-0 transition-[height] group-hover:h-[0%]" />
        <div className="w-full h-full absolute top-0 left-0 flex flex-col justify-center items-start px-4 group-hover:opacity-0 transition-opacity text-white">
          <h2 className="text-2xl" itemProp="headline">{title}</h2>
        </div>
      </article>
    </Link>
  );
}

export default function BlogGridTiles({ blok, pageContext = {} }) {
  const location = useLocation();
  const [showIndex, setShowIndex] = React.useState(0);

  const searchPostCount = 9;

  const currentLanguage = extractLanguage(location.pathname);

  const { component } = blok;
  const styles = clsx(`${component} px-4 lg:px-16`);

  library.add({ ...icons });

  const data = useStaticQuery(
    graphql`
      query {
        defaultPosts: allStoryblokEntry(
          filter: { field_component: { eq: "blogpost" }, lang: { eq: "default" } }
          limit: 10
          sort: { first_published_at: DESC }
        ) {
          edges {
            node {
              name
              full_slug
              slug
            }
          },
          pageInfo {
            currentPage
            pageCount
          }
        }
        enPosts: allStoryblokEntry(
          filter: { field_component: { eq: "blogpost" }, lang: { eq: "en" } }
          limit: 10
          sort: { first_published_at: DESC }
        ) {
          edges {
            node {
              name
              full_slug
              slug
            }
          }
          pageInfo {
            currentPage
            pageCount
          }
        }
        allStoryblokDatasourceEntry(filter: {data_source: {eq: "categories"}}) {
          nodes {
            name
            value
          }
        }
      }
    `,
  );

  const { tag } = pageContext;
  let categories = data?.allStoryblokDatasourceEntry?.nodes;
  categories = [
    {
      name: 'Alle',
    },
    ...categories,
  ];

  const currentCategory = categories.find((category) => category?.value === tag || (tag === undefined && category.name === 'Alle'));

  const currentPosts = currentLanguage === 'en' ? data.enPosts : data.defaultPosts;
  const posts = currentPosts.edges
    ? currentPosts.edges.map((edge) => edge.node) : currentPosts.map((node) => node);

  const currentPage = currentPosts.pageInfo?.currentPage;
  const [pageCount] = React.useState(currentPosts.pageInfo?.pageCount);

  return (
    <div {...storyblokEditable(blok)} className={styles}>
      <div className="mt-[60px] md:mt-20 mx-auto flex gap-4 md:gap-0 md:min-h-[40px] flex-wrap md:flex-nowrap">
        <div className="flex flex-row w-full md:w-1/2">
          {currentCategory && (
          <div className="text-sm md:text-base block whitespace-nowrap px-4 py-2 rounded bg-latori-green-dark text-white w-fit md:ml-0 mr-2">{currentCategory.name}</div>
          )}
          <Swiper
            className="cursor-grab swiper-blog"
            modules={[Navigation]}
            slidesPerView="auto"
            spaceBetween={10}
            navigation
            breakpoints={{
              1024: {
                slidesOffsetAfter: 32,
                slidesOffsetBefore: 0,
              },
            }}
          >
            {categories
              .filter((category) => (category.name !== currentCategory.name))
              .map((category) => (
                <SwiperSlide
                  key={`categories-${category.name}`}
                  className="w-auto"
                  style={{ width: 'auto' }}
                >
                  <Link to={category.name !== 'Alle' ? `/blog/${category.value}` : '/blog'} className={clsx('text-sm md:text-base block whitespace-nowrap px-4 py-2 rounded bg-background-light dark:bg-[#212121] hover:bg-latori-green-dark hover:text-white', category?.value === currentCategory?.value ? '!bg-latori-green-dark text-white' : '')}>{category.name}</Link>
                </SwiperSlide>
              ))}
          </Swiper>
        </div>
      </div>
      <div className="mt-[60px] lg:mt-20 grid grid-cols-1 xl:grid-cols-3 md:grid-cols-2 gap-2 md:gap-4">
        {posts.slice(0, ((showIndex + 1) * searchPostCount)).map((post) => {
          const title = post.name || post.slug;
          const image = post.hero;
          const { width, height } = getStoryblokDimensions(image);
          return (
            <Link
              to={`/${post.full_slug}`}
              className="mx-auto aspect-[134/85] w-full relative group overflow-hidden"
              key={title}
            >
              <article
                itemScope
                itemType="http://schema.org/Article"
                className="bg-slate-100 dark:bg-latori-green-dark shadow-lg h-full"
              >
                {image && (
                <img
                  alt={title}
                  src={image}
                  width={width}
                  height={height}
                  loading="lazy"
                  className="object-cover w-full h-full absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
                />
                )}
                <div className="bg-latori-green-dark opacity-90 w-full h-full absolute bottom-0 left-0 transition-[height] group-hover:h-[0%]" />
                <div className="w-full h-full absolute top-0 left-0 flex flex-col justify-center items-start px-4 group-hover:opacity-0 transition-opacity text-white">
                  <h3 className="text-2xl" itemProp="headline">
                    {title}
                  </h3>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
      {/* <BlogGridView posts={posts.slice(1)} /> */}
      {pageCount > 1 && (
      <div className="px-6 md:px-8 mt-[100px] md:text-center gap-4">
        {
                      currentPage > 1 && (
                        <Link to={currentPage - 1 === 1 ? `/blog${tag ? `/${handleize(tag)}` : ''}` : `/blog${tag ? `/${handleize(tag)}` : ''}?page/${currentPage - 1}`} className="inline-block">&lt; PREV</Link>
                      )
                    }
        {Array(pageCount).fill(0).map((_, i) => {
          const _page = i + 1;
          return (
            <Link
              key={`page-l-${_page}`}
              to={_page === 1 ? `/blog${tag ? `/${handleize(tag)}` : ''}` : `/blog${tag ? `/${handleize(tag)}` : ''}/page/${_page}`}
              className={`w-[42px] h-[42px] rounded-full inline-flex justify-center items-center ${_page === currentPage ? 'bg-latori-green-dark text-white' : ''}`}
            >
              {_page}
            </Link>
          );
        })}
        {
                      pageCount > currentPage && (
                        <Link to={currentPage + 1 === 1 ? `/blog${tag ? `/${handleize(tag)}` : ''}` : `/blog${tag ? `/${handleize(tag)}` : ''}/page/${currentPage + 1}`} className="inline-block">NEXT &gt;</Link>
                      )
                    }
      </div>
      )}
    </div>
  );
}
